import { ApiResult, PaginatedResponse, request, safeRequest } from '@synergy/api';
import { Media } from '@synergy/models';

import { BankAccount } from '../models/bank-accounts';
import { BankAccountErrorCode } from '../../../../models/bank-account-error-code';
import { BankAccountRequest } from './models/bank-account-request';
import { EmailErrorCode } from '../../../../models/email-error-code';
import { EmailErrorHandler } from '../../../../api/email-error-handler';
import { FtpUser } from '../models/ftp-user';
import { OverviewBankAccountErrorHandler } from '../../../../api/overview-bank-account-error-handler';
import { ParentSupplier } from '../models/parent-supplier';
import { SupplierDetail } from '../models/supplier-detail';
import { SupplierDetailOverviewRequest } from './models/supplier-detail-overview-request';
import { WhitelistedCustomer } from '../models/whitelisted-customer';

export class SupplierOverviewApi {
    constructor(protected baseUrl: string) {}

    public get(supplierId: number): Promise<ApiResult<SupplierDetail>> {
        return safeRequest(
            () => request.get<SupplierDetail>(`${this.baseUrl}/suppliers/${supplierId}/details`),
            response => response.data
        );
    }

    public patchDetail(
        supplierId: number,
        changes: SupplierDetailOverviewRequest
    ): Promise<ApiResult<SupplierDetail, EmailErrorCode>> {
        return safeRequest(
            () => request.patch(`${this.baseUrl}/suppliers/${supplierId}/details`, changes),
            response => response.data,
            EmailErrorHandler
        );
    }

    public createDetail(changes: SupplierDetailOverviewRequest): Promise<ApiResult<SupplierDetail, EmailErrorCode>> {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/suppliers`, changes, {
                    params: { isSync: true },
                }),
            response => response.data,
            EmailErrorHandler
        );
    }

    public deleteFtpUser(supplierId: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.delete(`${this.baseUrl}/suppliers/${supplierId}/ftp-user`),
            () => null
        );
    }

    public saveNewFtpPassword(supplierId: number, ftpPassword: string): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.patch(`${this.baseUrl}/suppliers/${supplierId}/ftp-user`, { ftpPassword }),
            () => null
        );
    }

    public addFtp(supplierId: number, ftpPassword: string): Promise<ApiResult<FtpUser>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/suppliers/${supplierId}/ftp-user`, { ftpPassword }),
            response => response.data
        );
    }

    public addBankAccount(
        supplierId: number,
        bankAccount: BankAccountRequest
    ): Promise<ApiResult<BankAccount, BankAccountErrorCode>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/suppliers/${supplierId}/add-bank-account`, { ...bankAccount }),
            response => response.data,
            OverviewBankAccountErrorHandler
        );
    }

    public updateBankAccount(
        supplierId: number,
        bankAccount: BankAccountRequest,
        id: number
    ): Promise<ApiResult<BankAccount, BankAccountErrorCode>> {
        return safeRequest(
            () =>
                request.patch(`${this.baseUrl}/suppliers/${supplierId}/update-bank-account/${id}`, {
                    ...bankAccount,
                }),
            response => response.data,
            OverviewBankAccountErrorHandler
        );
    }

    public deleteBankAccount(supplierId: number, bankAccountId: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.delete(`${this.baseUrl}/suppliers/${supplierId}/remove-bank-account/${bankAccountId}`),
            () => null
        );
    }

    public uploadFile(supplierId: number, bankAccountId: number, file: File): Promise<ApiResult<Media>> {
        return safeRequest(
            () => {
                const data = new FormData();
                data.append('file', file, file.name);

                const config = { headers: { 'content-type': 'multipart/form-data' } };

                return request.post(
                    `${this.baseUrl}/suppliers/${supplierId}/upload-bank-account-media/${bankAccountId}`,
                    data,
                    config
                );
            },
            response => response.data
        );
    }

    public searchWhitelistedCustomers(
        supplierId: number,
        params: URLSearchParams = new URLSearchParams()
    ): Promise<ApiResult<WhitelistedCustomer[]>> {
        return safeRequest(
            () =>
                request.get(`${this.baseUrl}/suppliers/${supplierId}/search-possible-whitelist-customers`, {
                    params,
                }),
            response => response.data
        );
    }

    public uploadProfileImage(supplierId: number, file: File): Promise<ApiResult<Media>> {
        return safeRequest(
            () => {
                const data = new FormData();
                data.append('file', file, file.name);

                const config = { headers: { 'content-type': 'multipart/form-data' } };

                return request.post(`${this.baseUrl}/suppliers/${supplierId}/upload-image`, data, config);
            },
            response => response.data
        );
    }

    public removeProfileImage(supplierId: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.delete(`${this.baseUrl}/suppliers/${supplierId}/remove-image`),
            () => null
        );
    }

    public getParentSuppliers(
        supplierId: number,
        params: URLSearchParams = new URLSearchParams()
    ): Promise<ApiResult<PaginatedResponse<ParentSupplier>>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/suppliers/${supplierId}/search-parent`, { params }),
            response => response.data
        );
    }
}
